// useHeartbeat — pings /badges/heartbeat every 60s while the tab is visible.
// Captures newly-earned badges and surfaces them via the onUnlock callback.
import { useEffect, useRef } from 'react';
import api from './studentApi';

export default function useHeartbeat({ onUnlock, intervalSec = 60, enabled = true }) {
  const lastPingRef = useRef(0);

  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;
    let timer = null;

    const ping = async () => {
      if (cancelled) return;
      if (document.visibilityState !== 'visible') return;
      const now = Date.now();
      const elapsed = lastPingRef.current ? Math.min(120, Math.round((now - lastPingRef.current) / 1000)) : intervalSec;
      lastPingRef.current = now;
      try {
        const r = await api.post('/student/badges/heartbeat', { seconds: elapsed });
        if (r?.newly_earned?.length) {
          // Filter out trophy_unlocked entries — those are handled separately
          const badges = r.newly_earned.filter(x => !x.trophy_unlocked);
          const trophies = r.newly_earned.filter(x => x.trophy_unlocked).map(x => x.trophy_unlocked);
          if (badges.length || trophies.length) onUnlock && onUnlock({ badges, trophies, summary: r });
        }
      } catch { /* network blip — ignore */ }
    };

    // First ping after intervalSec; subsequent every intervalSec
    timer = setInterval(ping, intervalSec * 1000);

    // Also ping when tab becomes visible after being hidden
    const onVis = () => { if (document.visibilityState === 'visible') ping(); };
    document.addEventListener('visibilitychange', onVis);

    return () => {
      cancelled = true;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [enabled, intervalSec, onUnlock]);
}
